import React from 'react';
import { Building2, TrendingUp, TrendingDown, ChevronRight, PanelLeftClose } from 'lucide-react';
import { Button } from '../ui/button';
import { formatSmartCurrency } from '../../utils/formatCurrency';
import { getDivisionColor } from './utils';

const DivisionSkeleton = () => (
  <div className="space-y-1">
    {[...Array(8)].map((_, i) => (
      <div key={i} className="w-full px-3 py-2.5 rounded-lg animate-pulse">
        <div className="flex items-center gap-2 mb-1">
          <div className="h-4 w-4 bg-gray-200 rounded" />
          <div className="h-3.5 bg-gray-200 rounded" style={{ width: `${60 + (i % 3) * 20}%` }} />
        </div>
        <div className="h-2.5 w-16 bg-gray-100 rounded mt-1" />
        <div className="flex justify-between mt-1">
          <div className="h-2.5 w-14 bg-gray-100 rounded" />
          <div className="h-3 w-10 bg-gray-100 rounded" />
        </div>
      </div>
    ))}
  </div>
);

const DiffBadge = ({ value, selected }) => {
  const isPositive = value >= 0;
  return (
    <div className={`flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-medium ${
      selected 
        ? 'bg-white/20' 
        : isPositive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
    }`}>
      {isPositive ? <TrendingUp className="h-2 w-2" /> : <TrendingDown className="h-2 w-2" />}
      {isPositive ? '+' : ''}{value.toFixed(1)}%
    </div>
  );
};

export const DivisionPanel = ({
  isOpen,
  setIsOpen,
  divisions,
  selectedDivision,
  onSelectDivision,
  loading = false
}) => (
  <div className={`${isOpen ? 'w-56' : 'w-12'} flex-shrink-0 bg-gray-50 border-r border-gray-200 transition-all duration-300 flex flex-col`}>
    {isOpen ? (
      <>
        <div className="p-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Divisions
          </h2>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setIsOpen(false)}>
            <PanelLeftClose className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="division-panel">
          {loading ? (
            <DivisionSkeleton />
          ) : (
            divisions.map(({ division, thisYearTotal, thisYearAdjusted, lastYearTotal, lyBudgetTotal, lySaleVsTyPct, lyBudgetVsTyPct, diffPercent, completed, total }) => {
              const isSelected = selectedDivision === division;
              const hasLyBudget = (lyBudgetTotal || 0) > 0;
              return (
                <button
                  key={division}
                  data-testid={`division-${division}`}
                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-200 ${
                    isSelected
                      ? `${getDivisionColor(division)} text-white shadow-md`
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                  onClick={() => onSelectDivision(division)}
                >
                  <div className="flex flex-col items-start w-full">
                    <div className="flex items-center gap-2 w-full">
                      <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="font-semibold text-xs">{division}</span>
                    </div>
                    <div className={`text-[9px] mt-0.5 ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>
                      {completed}/{total} completed
                    </div>
                    {/* Budget stats grid */}
                    <div className="w-full mt-1.5 space-y-0.5">
                      {hasLyBudget && (
                        <div className="flex items-center justify-between text-[9px]">
                          <span className={isSelected ? 'text-white/60' : 'text-gray-400'}>LY Bgt</span>
                          <span className={isSelected ? 'text-white/80' : 'text-gray-600'}>{formatSmartCurrency(lyBudgetTotal)}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between text-[9px]">
                        <span className={isSelected ? 'text-white/60' : 'text-gray-400'}>LY Sale</span>
                        <span className={isSelected ? 'text-white/80' : 'text-gray-600'}>{formatSmartCurrency(lastYearTotal)}</span>
                      </div>
                      <div className="flex items-center justify-between text-[9px]">
                        <span className={isSelected ? 'text-white/60' : 'text-gray-400'}>TY Bgt</span>
                        <span className={isSelected ? 'text-white/80' : 'text-gray-600'}>{formatSmartCurrency(thisYearTotal)}</span>
                      </div>
                      <div className="flex items-center justify-between text-[9px]">
                        <span className={isSelected ? 'text-white/60' : 'text-gray-400'}>TY Act.</span>
                        <span className={`font-semibold ${isSelected ? 'text-white' : 'text-gray-800'}`}>{formatSmartCurrency(thisYearAdjusted || thisYearTotal)}</span>
                      </div>
                      {/* Diff badges */}
                      <div className="flex items-center gap-1 mt-1">
                        {hasLyBudget && <DiffBadge value={lyBudgetVsTyPct || 0} selected={isSelected} />}
                        <DiffBadge value={lySaleVsTyPct || diffPercent || 0} selected={isSelected} />
                      </div>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </>
    ) : (
      <div className="flex flex-col items-center py-3 gap-2">
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setIsOpen(true)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
        <div className="writing-mode-vertical text-xs text-gray-500 font-medium tracking-wider" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
          DIVISIONS
        </div>
      </div>
    )}
  </div>
);
